import {Injectable} from '@angular/core';
import {BenutzerModels} from '../models/benutzer-models';
import {HttpClient, HttpHeaders, HttpParams, HttpStatusCode} from '@angular/common/http';
import {SwissAppConfigModels} from '../models/swissappconfig-models';
import {HelperService} from './helper.service';
import {LocalStorageService} from '../local-storage.service';
import {MessageBoxService} from './message-box.service';
import {interval, ReplaySubject, tap} from 'rxjs';
import {Router} from '@angular/router';
import {EmptyEmployee} from '../time-app/models/employees-model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Capacitor} from '@capacitor/core';
import BenutzerRecht = BenutzerModels.BenutzerRecht;
import EmptyAnwender = BenutzerModels.EmptyAnwender;
import EmptyMandant = SwissAppConfigModels.EmptyMandant;
import Anwender = BenutzerModels.Anwender;

@Injectable({
    providedIn: 'root'
})
export class UserAuthenticationService {
    public currentUserInfo: BenutzerModels.Benutzer = new BenutzerModels.EmptyUser();
    public userRights: any;

    private webServiceConfig?: SwissAppConfigModels.WebServiceConfig;
    private STORAGE_WEB_CONFIG = 'SwissWebConfigDB';
    private currentAuthenticatedUser?: BenutzerModels.Anwender;
    private currentMandant?: SwissAppConfigModels.SwissAppMandant;
    private rightsByGroup: BenutzerModels.RechtGruppeReihenfolge[] = [];
    private userGroups: BenutzerModels.BenutzerGruppe[] = [];
    private checkForOtherLoginInterval?: NodeJS.Timeout;
    private currentUser$: ReplaySubject<BenutzerModels.Anwender | undefined> = new ReplaySubject<BenutzerModels.Anwender | undefined>(1);

    constructor(private http: HttpClient, private helperService: HelperService, private localStorageService: LocalStorageService, private svcMessageBox: MessageBoxService, private router: Router) {
        this.helperService.getSwissWebServiceConfig$().pipe(takeUntilDestroyed(), tap((config) => {
            this.webServiceConfig = config;
        })).subscribe();
        this.getCurrentUser().subscribe({
            next: (user) => {
                if (!user) {
                    this.currentUserInfo = new BenutzerModels.EmptyUser();
                }
            }
        });
    }

    public loginUser(userInfo: BenutzerModels.Benutzer): Promise<boolean> {
        return new Promise((resolve) => {
            const headers = new HttpHeaders().set('Content-Type', 'application/json').set('isWebserviceAvailableCheck', 'true');

            const options = {
                headers,
            };

            if (userInfo.userPW === '1MLCW0T0Q8X9kC4F88L4WQ==' && userInfo.userName === '3MylPKUvEf3fwqRjd3JnvQ==' && Capacitor.isNativePlatform()) {
                return new Promise(async () => {
                    this.currentUserInfo = userInfo;
                    this.currentUserInfo.clearName = 'demo';
                    this.currentMandant = new EmptyMandant();
                    await this.localStorageService.add('allEmployees', [new EmptyEmployee()], 'SwissTimeEntry');

                    await this.localStorageService.add('NotFirstAppStart', true, this.STORAGE_WEB_CONFIG, false, true);
                    await this.localStorageService.add('userAuthenticated', true, this.STORAGE_WEB_CONFIG, false, true);
                    await this.localStorageService.add('userName', userInfo.userName, this.STORAGE_WEB_CONFIG, false, true);
                    await this.localStorageService.add('userPW', userInfo.userPW, this.STORAGE_WEB_CONFIG, false, true);
                    resolve(true);
                });
            }


            const url = this.webServiceConfig!.userUrl;
            return this.http.post<{ value: boolean }>(url + '/UserLogin', userInfo, options).subscribe({
                next: async (validLoginAttempt) => {
                    if (validLoginAttempt && validLoginAttempt.value) {
                        this.currentUserInfo = userInfo;
                        this.currentUserInfo.clearName = this.decryptUserName(this.currentUserInfo.userName);
                        await this.localStorageService.add('NotFirstAppStart', true, this.STORAGE_WEB_CONFIG, false, true);
                        await this.localStorageService.add('userAuthenticated', true, this.STORAGE_WEB_CONFIG, false, true);
                        await this.localStorageService.add('userName', userInfo.userName, this.STORAGE_WEB_CONFIG, false, true);
                        await this.localStorageService.add('userPW', userInfo.userPW, this.STORAGE_WEB_CONFIG, false, true);
                        await this.localStorageService.add('autoLogin', true, this.STORAGE_WEB_CONFIG, false, true);
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                }, error: () => {
                    this.offlineLogin(userInfo).then(res => {
                        if (res) {
                            this.currentUserInfo = userInfo;
                        }
                        resolve(res);
                    });
                }
            });
        });
    }

    public checkLogin(userInfo: BenutzerModels.Benutzer): Promise<boolean> {
        return new Promise((resolve) => {
            const headers = new HttpHeaders().set('Content-Type', 'application/json').set('isWebserviceAvailableCheck', 'true');

            const options = {
                headers,
            };

            if (userInfo.userPW == '1MLCW0T0Q8X9kC4F88L4WQ==' && userInfo.userName == '3MylPKUvEf3fwqRjd3JnvQ==' && Capacitor.isNativePlatform()) {
                resolve(true);
            }

            const url = this.webServiceConfig!.userUrl;
            return this.http.post<{ value: boolean }>(url + '/UserLogin', userInfo, options).subscribe({
                next: async (validLoginAttempt) => {
                    resolve(validLoginAttempt && validLoginAttempt.value);
                }, error: async () => {
                    resolve(await this.offlineLogin(userInfo));
                }
            });
        });
    }

    public async offlineLogin(user: BenutzerModels.Benutzer): Promise<boolean> {
        const storedUsername = await this.localStorageService.get('userName', this.STORAGE_WEB_CONFIG, true, '');
        const storedPW = await this.localStorageService.get('userPW', this.STORAGE_WEB_CONFIG, true, '');
        return user.userName === storedUsername && user.userPW === storedPW && user.userName.length > 0 && user.userPW.length > 0;
    }

    public async checkUserLicenses(userInfo: BenutzerModels.Benutzer = this.currentUserInfo): Promise<boolean> {
        if (userInfo.userName == '') {
            return false;
        }
        if (userInfo.userName == 'demo') {
            return true;
        }
        const benutzerAnwendung = await this.getUserLicenses();
        if (benutzerAnwendung.otherRegistrationExists) {
            const removeOtherReg = await this.svcMessageBox.askYesNoQuestion('MAIN_APP_DIALOG.USER_LOGIN.msg', 'MAIN_APP_DIALOG.USER_LOGIN.title');
            if (removeOtherReg) {
                const removeSuccessful = await this.removeOtherRegistrations(userInfo);
                if (removeSuccessful) {
                    await this.getUserLicenses();
                    return true;
                } else {
                    return false;
                }
            } else {
                await this.removeMyRegistration(userInfo);
                return false;
            }
        } else {
            return true;
        }
    }

    public async checkAuthenticated(): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.currentAuthenticatedUser) {
                resolve(true);
            } else {
                // check if user is set in indexDB
                this.localStorageService.get('userAuthenticated', this.STORAGE_WEB_CONFIG, false, false).then(async (authenticated) => {
                    if (!authenticated) {
                        return resolve(false);
                    }
                    if (this.currentUserInfo.userName == '' && this.currentUserInfo.userPW == '') {
                        await this.loadUserInfoFromIndexDB();
                    }
                    this.loadAnwenderData(this.currentUserInfo).then(() => {
                        this.checkUserLicenses(this.currentUserInfo).then(hasLicense => {
                            resolve(hasLicense);
                        });
                    }).catch(() => resolve(false));
                }).catch(() => resolve(false));
            }
        });
    }

    public async loadAnwenderData(userInfo: BenutzerModels.Benutzer = this.currentUserInfo): Promise<BenutzerModels.Anwender> {
        try {
            if (userInfo.clearName == 'demo' && Capacitor.isNativePlatform()) {
                await this.localStorageService.add('userAnwendung', {
                    benutzerLizenzen: [{lizenzName: 'CMobileTime'}],
                    benutzerModule: [{iD_SwissModule: 2, module: 'vinavon Zeit'}],
                    otherRegistrationExists: false
                }, this.STORAGE_WEB_CONFIG, false, true);
                return new EmptyAnwender();
            }

            const user = await this.getUserData(userInfo);
            this.currentUserInfo.mandant = user.mandator;
            this.currentUserInfo.mandantID = user.mandatorID;

            this.setCurrentAuthenticatedUser(user);
            await this.getUserRights();
            this.startCheckingForOtherLogins();
            if (!this.currentUserInfo.mandant || !this.currentMandant) {
                const mandanten = await this.getSwissAppMandanten();
                const mandant = mandanten.filter((m) => m.mandatorID === this.currentUserInfo.mandantID)[0];
                this.currentMandant = mandant;
                this.currentUserInfo.mandant = mandant?.description;
                await this.localStorageService.add('userMandant', this.currentMandant.mandatorID, this.STORAGE_WEB_CONFIG, false, true);

                return user;
            } else {
                return user;
            }
        } catch (err) {
            throw err;
        }
    }

    public async getCurrentUserLicense(): Promise<BenutzerModels.BenutzerLizenz[]> {
        const anwendung: BenutzerModels.BenutzerAnwendung = await this.localStorageService.get('userAnwendung', this.STORAGE_WEB_CONFIG, true, false);
        return anwendung?.benutzerLizenzen || [];
    }

    public async getSwissAppMandanten(): Promise<SwissAppConfigModels.SwissAppMandant[]> {
        return new Promise((resolve) => {
            const headers = new HttpHeaders().set('Content-Type', 'application/json');

            const options = {
                headers,
            };

            const url = this.webServiceConfig!.manUrl;
            this.http.get<SwissAppConfigModels.SwissAppMandant[]>(url + '/MandantenData', options).subscribe({
                next: (mandanten: SwissAppConfigModels.SwissAppMandant[]) => {
                    this.localStorageService.add('mandanten', mandanten, this.STORAGE_WEB_CONFIG, false, true);
                    resolve(mandanten);
                }, error: () => {
                    this.localStorageService.get('mandanten', this.STORAGE_WEB_CONFIG, true, []).then((mandanten: any) => resolve(mandanten));
                }
            });
        });
    }

    public getCurrentAuthenticatedUser(): BenutzerModels.Anwender | undefined {
        return this.currentAuthenticatedUser;
    }

    public getCurrentUser(): ReplaySubject<Anwender | undefined> {
        return this.currentUser$;
    }

    public getUserRights(user: BenutzerModels.Anwender | undefined = this.currentAuthenticatedUser): Promise<BenutzerModels.BenutzerRecht[]> {
        return new Promise((resolve, reject) => {
            if (!user) {
                reject();
            }

            const headers = new HttpHeaders({
                Authorization: this.helperService.getSwissAuthorization(this.currentUserInfo),
            });

            const params: HttpParams = new HttpParams()
                .set('MandantID', this.currentAuthenticatedUser!.mandatorID)
                .set('EmployeeID', this.currentAuthenticatedUser!.employeeID.toString())
                .set('UserID', (this.currentAuthenticatedUser!.userID || 0).toString());

            const url = this.webServiceConfig!.userUrl;
            return this.http.get<BenutzerModels.BenutzerRecht[]>(url + '/UserRights', {
                headers, params,
            }).subscribe({
                next: (rights: BenutzerModels.BenutzerRecht[]) => {
                    this.localStorageService.add('benutzerRechte', rights, this.STORAGE_WEB_CONFIG);
                    this.userRights = rights;
                    resolve(rights);
                }, error: () => {
                    this.localStorageService.get('benutzerRechte', this.STORAGE_WEB_CONFIG, false, []).then((rights) => {
                        this.userRights = rights;
                        resolve(rights);
                    });
                }
            });

        });
    }

    public updateCurrentUser(user: BenutzerModels.Anwender): Promise<boolean> {
        return new Promise(resolve => {
            if (!this.helperService.isWebServiceAvailable()) {
                return resolve(false);
            }

            const url = this.webServiceConfig!.anwUrl;
            return this.http.post<boolean>(url + '/UserData', user, this.helperService.getSwissHttpOptions(this.currentUserInfo)).subscribe({
                next: (res) => {
                    return resolve(res);
                }, error: () => {
                    return resolve(false);
                }
            });
        });
    }

    public getUserObjectRights(objectID: string): Promise<BenutzerModels.BenutzerRechtObjekt[]> {
        return new Promise((resolve, reject) => {
            const headers = new HttpHeaders({
                Authorization: this.helperService.getSwissAuthorization(this.currentUserInfo),
            });

            const params: HttpParams = new HttpParams()
                .set('MandantID', this.currentAuthenticatedUser!.mandatorID)
                .set('EmployeeID', this.currentAuthenticatedUser!.employeeID.toString())
                .set('UserID', this.currentAuthenticatedUser!.userID.toString())
                .set('ObjectID', objectID);

            const url = this.webServiceConfig!.userUrl;
            this.http.get<BenutzerModels.BenutzerRechtObjekt[]>(url + '/UserObjectRights', {
                headers, params,
            }).subscribe({
                next: (userObjectRights: BenutzerModels.BenutzerRechtObjekt[]) => {
                    resolve(userObjectRights);
                }, error: (err) => reject(err)
            });

        });
    }

    public getRight(userRight: BenutzerModels.UserRightCodes): boolean {
        if (!this.userRights) {
            return false;
        }
        const benutzerRecht: BenutzerModels.BenutzerRecht[] = this.userRights.filter((br: BenutzerRecht) => br.rightsID === userRight);
        if (benutzerRecht && benutzerRecht.length > 0) {
            return benutzerRecht[0].effectiveValue === -1;
        } else {
            return false;
        }
    }

    public getUserData(user = this.currentUserInfo): Promise<BenutzerModels.Anwender> {
        return new Promise((resolve, reject) => {
            const url = this.webServiceConfig!.anwUrl;
            this.http.get<BenutzerModels.Anwender>(url + '/UserData', this.helperService.getSwissHttpOptions(user)).subscribe({
                next: async (user: BenutzerModels.Anwender) => {
                    await this.localStorageService.add('anwender', user, this.STORAGE_WEB_CONFIG, false, true);
                    await this.localStorageService.delete('datum', 'SwissTimeEntry', true);
                    this.setCurrentAuthenticatedUser(user);
                    resolve(user);
                }, error: () => {
                    this.localStorageService.get('anwender', this.STORAGE_WEB_CONFIG, true).then(async (user: BenutzerModels.Anwender) => {
                        await this.localStorageService.delete('datum', 'SwissTimeEntry', true);
                        this.setCurrentAuthenticatedUser(user);
                        resolve(user);
                    }).catch(err => reject(err));
                }
            });
        });
    }

    public getUserDayNotes(date: string): Promise<any> {
        return new Promise(resolve => {
            const url = this.webServiceConfig!.anwUrl;
            const headers = new HttpHeaders({
                Authorization: this.helperService.getSwissAuthorization(this.currentUserInfo),
            });
            const params: HttpParams = new HttpParams()
                .set('date', date)
                .set('MandantID', this.currentAuthenticatedUser!.mandatorID);

            return this.http.get<any>(url + '/GetUserDayNotes', {headers, params,}).subscribe({
                next: (res) => {
                    this.localStorageService.add('userDayNotes', res, this.STORAGE_WEB_CONFIG);
                    resolve(res);
                }, error: () => {
                    this.localStorageService.get('userDayNotes', this.STORAGE_WEB_CONFIG, false, []).then(userDayNotes => {
                        resolve(userDayNotes);
                    });
                }
            });
        });
    }

    public async removeOtherRegistrations(userInfo: BenutzerModels.Benutzer = this.currentUserInfo) {
        return new Promise(resolve => {
            const headers = new HttpHeaders().set('Content-Type', 'application/json');
            const params = new HttpParams()
                .set('userName', userInfo.clearName || this.decryptUserName(this.currentUserInfo.userName))
                .set('userSessionToken', this.helperService.getUserSessionToken());

            const url = this.webServiceConfig!.userUrl;
            return this.http.get<Boolean>(url + '/RemoveUserLicenses', {headers, params}).subscribe({
                next: (value) => {
                    resolve(value);
                }, error: () => {
                    resolve(!this.helperService.isWebServiceAvailable());
                }
            });
        });
    }

    public async removeMyRegistration(userInfo: BenutzerModels.Benutzer = this.currentUserInfo) {
        return new Promise(resolve => {
            const headers = new HttpHeaders().set('Content-Type', 'application/json');

            const params: HttpParams = new HttpParams()
                .set('userName', userInfo.clearName || this.decryptUserName(this.currentUserInfo.userName))
                .set('userSessionToken', this.helperService.getUserSessionToken());

            const url = this.webServiceConfig!.userUrl;
            this.http.get<Boolean>(url + '/RemoveMyUserLicense', {headers, params,}).subscribe({
                next: () => {
                    resolve(true);
                }, error: () => {
                    resolve(false);
                }
            });
        });
    }

    public async hasLicences(licenseName: BenutzerModels.UserLicenses): Promise<boolean> {
        const anwendung: BenutzerModels.BenutzerAnwendung = await this.localStorageService.get('userAnwendung', this.STORAGE_WEB_CONFIG, true, false);
        if (!anwendung) {
            return false;
        }
        return !!(anwendung.benutzerLizenzen.find((mod) => mod.lizenzName === licenseName));
    }

    public startCheckLoginState() {
        return interval(30000).subscribe(() => {
            this.checkLoginState();
        });
    }

    public async checkLoginState() {
        const username = this.decryptUserName(this.currentUserInfo.userName);
        if (!username) {
            return;
        }
        try {
            const res: any = await this.getUserLicenses();
            if (res === false) {
                await this.logoutUser('/login', true);
            }
        } catch {
            // backend is stupid and throws errors if username and pw doesn't match anyone instead of returning false
        }
    }

    public getWorkerNotes(employeeID: string): Promise<any> {
        return new Promise(resolve => {
            const url = this.webServiceConfig!.anwUrl;
            const headers = new HttpHeaders({
                Authorization: this.helperService.getSwissAuthorization(this.currentUserInfo),
            });
            const params: HttpParams = new HttpParams()
                .set('employeeID', employeeID)
                .set('MandantID', this.currentAuthenticatedUser!.mandatorID);

            return this.http.get<any>(url + '/GetWorkerNote', {headers, params,}).subscribe({
                next: (workerNote) => {
                    this.localStorageService.add('workerNote', workerNote, this.STORAGE_WEB_CONFIG, false, false);
                    resolve(workerNote);
                }, error: () => {
                    this.localStorageService.get('workerNote', this.STORAGE_WEB_CONFIG, false, '').then(res => {
                        resolve(res);
                    });
                }
            });
        });
    }

    async logoutUser(redirect: string = '/login', showOfflineLogoutNotPossibleError = false): Promise<void> {
        if (!navigator.onLine || !this.helperService.isWebServiceAvailable()) {
            if (!showOfflineLogoutNotPossibleError) {
                this.svcMessageBox.showErrorByLanguageKey('MAIN_APP_LOGIN.LOGOUT_OFFLINE', 'MAIN_APP_LOGIN.LOGOUT_ERROR');
            }
            return;
        }
        try {
            if (await this.logoutUserWebSrv()) {
                await this.localStorageService.delete('user_session_guid', this.STORAGE_WEB_CONFIG, true);
                await this.localStorageService.delete('pageRefreshURL', this.STORAGE_WEB_CONFIG, true);
                await this.localStorageService.delete('unlockedObjects', this.STORAGE_WEB_CONFIG, true);
                await this.localStorageService.delete('NotFirstAppStart', this.STORAGE_WEB_CONFIG);
            }
        } catch (err) {
            this.svcMessageBox.showErrorByLanguageKey('MAIN_APP_LOGIN.LOGOUT_ERROR', 'MAIN_APP_LOGIN.LOGOUT_ERROR');
            console.error(err);
        } finally {
            clearInterval(this.checkForOtherLoginInterval);
            await this.localStorageService.delete('autoLogin', this.STORAGE_WEB_CONFIG, true);
            this.currentAuthenticatedUser = undefined;
            this.currentUser$.next(undefined);
            await this.router.navigate([redirect]);
        }
    }

    public startCheckingForOtherLogins() {
        if (this.checkForOtherLoginInterval) {
            return;
        }
        this.checkForOtherLoginInterval = setInterval(async () => {
            const stillActiveLogin = await this.checkForOtherLogin(this.currentUserInfo);
            if (!stillActiveLogin && this.currentUserInfo.clearName != 'demo') {
                await this.logoutUser();
            }
        }, 30000);
    }

    public getCurrentMandant(): SwissAppConfigModels.SwissAppMandant {
        return this.currentMandant!;
    }

    public decryptUserName(userName: string) {
        // WebLoginName von Employees
        const name = this.helperService.decryptUsingAES256(userName);
        return name.substring(1, name.length - 1).toString();
    }

    public getUserGroups(): Promise<BenutzerModels.BenutzerGruppe[]> {
        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.currentUserInfo),
        });

        const params: HttpParams = new HttpParams()
            .set('EmployeeID', this.currentAuthenticatedUser!.employeeID.toString());

        if (this.currentAuthenticatedUser!.userID) {
            params.set('UserID', this.currentAuthenticatedUser!.userID.toString());
        }

        const options = {
            headers, params,
        };

        const url = this.webServiceConfig!.userUrl;
        return new Promise(resolve => {
            this.http.get<BenutzerModels.BenutzerGruppe[]>(url + '/UserGroups', options).subscribe({
                next: (userGroups) => {
                    this.localStorageService.add('benutzerGruppen', userGroups, this.STORAGE_WEB_CONFIG);
                    this.userGroups = userGroups;
                    resolve(this.userGroups);
                }, error: () => {
                    this.localStorageService.get('benutzerGruppen', this.STORAGE_WEB_CONFIG, false, []).then((value) => {
                        this.userGroups = (value as BenutzerModels.BenutzerGruppe[]);
                        resolve(this.userGroups);
                    });
                }
            });
        });
    }

    public getRightsGroupsOrder(): Promise<BenutzerModels.RechtGruppeReihenfolge[]> {
        const url = this.webServiceConfig!.userUrl;
        return new Promise((resolve) => {
            this.http.get<BenutzerModels.RechtGruppeReihenfolge[]>(url + '/GroupsRightsOrder', this.helperService.getSwissHttpOptions(this.currentUserInfo)).subscribe({
                next: (res) => {
                    this.localStorageService.add('rechteGruppen', res, this.STORAGE_WEB_CONFIG);
                    this.rightsByGroup = res;
                    resolve(this.rightsByGroup);
                }, error: () => {
                    this.localStorageService.get('rechteGruppen', this.STORAGE_WEB_CONFIG, false, []).then((value) => {
                        this.rightsByGroup = (value as BenutzerModels.RechtGruppeReihenfolge[]);
                        resolve(value);
                    });
                }
            });

        });
    }

    public async tryReLogin(): Promise<boolean> {
        const userData = await Promise.all([
            this.localStorageService.get('userName', this.STORAGE_WEB_CONFIG, true, ''),
            this.localStorageService.get('userPW', this.STORAGE_WEB_CONFIG, true, ''),
            this.localStorageService.get('userMandant', this.STORAGE_WEB_CONFIG, true, []),
            this.localStorageService.get('autoLogin', this.STORAGE_WEB_CONFIG, true, false)
        ]);
        if (userData[3]) {
            return await this.loginUser({userName: userData[0], userPW: userData[1], mandant: '', mandantID: userData[2]});
        }
        return false;
    }

    public getUserLicenses(userInfo: BenutzerModels.Benutzer = this.currentUserInfo): Promise<BenutzerModels.BenutzerAnwendung> {
        return new Promise((resolve) => {
            const headers = new HttpHeaders().set('Content-Type', 'application/json').set('isWebserviceAvailableCheck', 'true');
            const params = new HttpParams().set('userName', userInfo.clearName || this.decryptUserName(this.currentUserInfo.userName)).set('userSessionToken', this.helperService.getUserSessionToken());
            return this.http.get<BenutzerModels.BenutzerAnwendung>(this.webServiceConfig!.userUrl + '/UserLicenses', {headers, params,}).subscribe({
                next: async (anmeldung: BenutzerModels.BenutzerAnwendung) => {
                    await this.localStorageService.add('userAnwendung', anmeldung, this.STORAGE_WEB_CONFIG, false, true);
                    resolve(anmeldung);
                }, error: () => {
                    this.localStorageService.get('userAnwendung', this.STORAGE_WEB_CONFIG, true, {benutzerLizenzen: [], otherRegistrationExists: false, benutzerModule: []}).then((userAnwendung: any) => {
                        resolve(userAnwendung);
                    });
                }
            });
        });
    }

    private setCurrentAuthenticatedUser(user: BenutzerModels.Anwender | undefined): void {
        this.currentAuthenticatedUser = user;
        this.currentUser$.next(user);
    }

    private async loadUserInfoFromIndexDB(): Promise<BenutzerModels.Benutzer> {
        const userName = await this.localStorageService.get('userName', this.STORAGE_WEB_CONFIG, true, '');
        const userPW = await this.localStorageService.get('userPW', this.STORAGE_WEB_CONFIG, true, '');
        if (userName && userPW) {
            this.currentUserInfo.userName = userName;
            this.currentUserInfo.userPW = userPW;
            return this.currentUserInfo;
        } else {
            throw new Error('no indexed user data');
        }
    }

    private async logoutUserWebSrv(): Promise<boolean> {
        return new Promise(resolve => {
            const headers = new HttpHeaders().set('Content-Type', 'application/json');
            const url = this.webServiceConfig!.userUrl;
            return this.http.post<BenutzerModels.Benutzer>(url + '/UserLogout?userSessionToken=' + this.helperService.getUserSessionToken(), this.currentUserInfo, {
                headers,
            }).subscribe({
                next: () => {
                    resolve(true);
                }, error: () => {
                    resolve(false);
                }
            });
        });
    }

    private async checkForOtherLogin(userInfo = this.currentUserInfo) {
        return await this.checkCurrentUserRegistration(userInfo);
    }

    private async checkCurrentUserRegistration(userInfo = this.currentUserInfo): Promise<boolean> {
        const headers = new HttpHeaders().set('Content-Type', 'application/json').set('isWebserviceAvailableCheck', 'true');
        const params: HttpParams = new HttpParams().set('userName', userInfo.clearName || this.decryptUserName(this.currentUserInfo.userName)).set('userSessionToken', this.helperService.getUserSessionToken());

        const url = this.webServiceConfig!.userUrl;

        return new Promise(resolve => {
            if (!this.helperService.isWebServiceAvailable()) {
                resolve(true);
            } else {
                this.http.get<boolean>(url + '/CheckUserLicense', {headers, params}).subscribe({
                    next: (result) => {
                        resolve(result);
                    }, error: (err) => {
                        if (err.status === 0 || err.status === HttpStatusCode.GatewayTimeout) {  // ngsw returns 504 Gateway Timeout when the client is offline (https://github.com/angular/angular/issues/46445)
                            return resolve(true);
                        }
                        resolve(false);
                    }
                });
            }
        });
    }
}
